const audFmt = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});
const audFmtFull = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const aud = (n) => (n === null || n === undefined ? "—" : audFmt.format(n));
export const audFull = (n) => (n === null || n === undefined ? "—" : audFmtFull.format(n));

export const fmtDate = (str) => {
  if (!str) return "—";
  try {
    const d = new Date(str.length === 10 ? `${str}T00:00:00` : str);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return str;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

export const toDateStr = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Timezone-safe: add N days to a YYYY-MM-DD string (no UTC conversion glitches)
export const addDays = (dateStr, days) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return toDateStr(d);
};

export const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const JOB_STATUS_META = {
  quoting: { label: "Quoting", cls: "bg-amber-500/15 text-amber-300 border-amber-500/25" },
  scheduled: { label: "Scheduled", cls: "bg-sky-500/15 text-sky-300 border-sky-500/25" },
  in_progress: { label: "In progress", cls: "bg-teal-500/15 text-teal-300 border-teal-500/25" },
  on_hold: { label: "On hold", cls: "bg-slate-500/15 text-slate-300 border-slate-500/25" },
  completed: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" },
};

// Jobs board lifecycle tiers. Active = sites actually being built; won jobs waiting to
// start sit with the leads under "Quoting & upcoming"; Paused (on hold) and Completed are parked.
// The same four tiers drive the Job Switcher bar (lib/jobTiers.js).
export const JOB_BUCKETS = [
  {
    key: "active",
    label: "Active",
    short: "Active",
    hint: "Currently under construction",
    statuses: ["in_progress"],
    defaultStatus: "in_progress",
    reorderable: true, // drag rows up/down to set a site priority order (stored as job.board_order)
    empty: "No sites on the tools right now — drag a job here (or set it to In progress) once work starts.",
    defaultOpen: true,
  },
  {
    key: "quoting",
    label: "Quoting & upcoming",
    short: "Quoting",
    hint: "Leads being priced & won jobs waiting to start",
    statuses: ["quoting", "scheduled"],
    defaultStatus: "quoting",
    empty: "Nothing being quoted or waiting to start — drop a set of plans above or create a new job.",
    defaultOpen: true,
  },
  {
    key: "on_hold",
    label: "Paused",
    short: "Paused",
    hint: "On hold — waiting on the client, permits or finance",
    statuses: ["on_hold"],
    defaultStatus: "on_hold",
    empty: "No paused jobs.",
    defaultOpen: true,
  },
  {
    key: "completed",
    label: "Completed",
    short: "Completed",
    hint: "Finished builds",
    statuses: ["completed"],
    defaultStatus: "completed",
    empty: "No completed jobs yet.",
    defaultOpen: true,
  },
];

export const bucketForStatus = (status) =>
  (JOB_BUCKETS.find((b) => b.statuses.includes(status)) || JOB_BUCKETS[1]).key;

// "just now" · "4 min ago" · "3 hr ago" · "2 days ago" · "12 Aug 2026"
export const timeAgo = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "yesterday";
  if (d < 14) return `${d} days ago`;
  if (d < 60) return `${Math.round(d / 7)} wk ago`;
  return fmtDate(iso);
};

export const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Delivery workflow — shared across job tab, global page and Gantt markers
export const DELIVERY_STATUSES = [
  { value: "ordered", label: "Ordered", color: "#9ca3af", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/25" },
  { value: "confirmed", label: "Confirmed", color: "#38bdf8", cls: "bg-sky-500/15 text-sky-300 border-sky-500/25" },
  { value: "in_transit", label: "In transit", color: "#B8864B", cls: "bg-amber-500/15 text-amber-300 border-amber-500/25" },
  { value: "delivered", label: "Delivered", color: "#34d399", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" },
  { value: "delayed", label: "Delayed", color: "#f87171", cls: "bg-red-500/15 text-red-300 border-red-500/25" },
];
export const DELIVERY_STATUS_META = Object.fromEntries(DELIVERY_STATUSES.map((s) => [s.value, s]));
